import type { UiConfig } from '../config/ui.config';
import { el, formatNumber } from './dom';
import { ICONS, icon } from './icons';
import { LeaderboardList, type LeaderboardEntry, type OwnLeaderboardEntry } from './Leaderboard';
import { closeOnBackdrop, menuButton, menuFooter } from './MenuParts';

export interface LeaderboardScreenActions {
  readonly onRegister: () => void;
  readonly onBack: () => void;
  /** Signed in: "Abmelden" forgets the Spielausweis on this device. */
  readonly onSignOut: () => void;
}

/**
 * Leaderboard page (opened from the start screen, on the same backdrop): the server-side leaderboard of signed-in users
 * of companies registered on ructec.com (company logo and name, short name, score; ructec 2026-10-08), plus the personal
 * best stored on this device.
 */
const LEAD_SIGNED_OUT = 'Mit dem ructec-Konto deines Unternehmens kommst du in die Bestenliste – mit Firmenlogo und deinem Kürzel.';
const LEAD_SIGNED_IN = 'Du bist angemeldet – jeder Lauf zählt für die Bestenliste.';

export class LeaderboardScreen {
  readonly element: HTMLDivElement;
  private readonly list: LeaderboardList;
  private readonly best: HTMLElement;
  private readonly lead: HTMLElement;
  private readonly registerButton: HTMLElement;
  private readonly signOutButton: HTMLElement;

  constructor(parent: HTMLElement, ui: UiConfig, actions: LeaderboardScreenActions) {
    this.element = el('div', 'screen menu menu-sub menu-leaderboard', undefined, parent);
    const panel = el('section', 'sub-panel', undefined, this.element);
    panel.setAttribute('aria-labelledby', 'leaderboard-title');
    const title = el('h2', 'sub-title', undefined, panel);
    title.id = 'leaderboard-title';
    title.append(icon(ICONS.trophy, 'sub-title-icon'), 'Bestenliste');
    this.lead = el('p', 'sub-lead', LEAD_SIGNED_OUT, panel);
    this.list = new LeaderboardList(panel, ui.leaderboard.listSize, 'is-full');
    const bestLine = el('p', 'sub-best', 'Dein Rekord auf diesem Gerät: ', panel);
    this.best = el('strong', undefined, '0', bestLine);
    const buttons = el('div', 'sub-actions', undefined, panel);
    this.registerButton = menuButton(buttons, 'Anmelden & mitspielen', actions.onRegister, 'is-primary');
    menuButton(buttons, 'Zurück', actions.onBack, 'is-secondary');
    this.signOutButton = menuButton(buttons, 'Abmelden', actions.onSignOut, 'is-secondary');
    this.signOutButton.hidden = true;
    menuFooter(this.element, ui.footer);
    closeOnBackdrop(this.element, actions.onBack);
  }

  /** Signed in: "angemeldet als …" instead of the sign-in hint, no sign-in button. */
  setSignedIn(signedIn: boolean, player: { readonly company: string; readonly name: string } | null): void {
    this.lead.textContent = signedIn
      ? player ? `Angemeldet als ${player.name} · ${player.company} – jeder Lauf zählt für die Bestenliste.` : LEAD_SIGNED_IN
      : LEAD_SIGNED_OUT;
    this.registerButton.hidden = signedIn;
    this.signOutButton.hidden = !signedIn;
  }

  setBestScore(score: number): void {
    this.best.textContent = `${formatNumber(score)} Punkte`;
  }

  /** Top entries plus the signed-in player's own place (green; below the list with its rank if outside). */
  setEntries(entries: readonly LeaderboardEntry[], own: OwnLeaderboardEntry | null): void {
    this.list.setEntries(entries, own);
  }
}
